import _ from "lodash";
import { Iterable, List, Map } from "immutable";
import { nullish } from "./nullish";

// CAVEAT: Empty `items` array will return true
export const areAllItemsContainedInList = (items: string[] | any[] = [], list: string[] | any[] = []): boolean =>
    !items.some((item) => !list.includes(item));

export const translateCellBooleanInput = (value) => {
    if (typeof value === "boolean") {
        return value;
    }
    let flagValue = null;
    if (value !== undefined) {
        const cellValue = value.toLowerCase();
        if (["x", "1", "true", "yes"].includes(cellValue)) {
            flagValue = true;
        } else if (["", "0", "false", "no"].includes(cellValue)) {
            flagValue = false;
        }
    }
    return flagValue;
};

export type ImmutableIterable = List<any> | Map<string, any>;
// Helper to get object by paths from immutable map or POJO
export const amphibiousGetIn = (
    data: object | ImmutableIterable,
    paths: Array<string | number>,
    defaultValue?: any
) => {
    if (Iterable.isIterable(data)) {
        return (data as ImmutableIterable).getIn(paths, defaultValue);
    }

    return _.get(data, paths, defaultValue);
};

// Helper to get object from immutable map or POJO
export const amphibiousGet = (data: object | ImmutableIterable, path: string | number, defaultValue?: any) => {
    if (Iterable.isIterable(data)) {
        return (data as ImmutableIterable).getIn([path], defaultValue);
    }

    return _.get(data, [path], defaultValue);
};

// Helper to set object by paths from immutable map or POJO
export const amphibiousSetIn = (
    data: object | ImmutableIterable,
    paths: Array<string | number>,
    value: any,
    immutablePOJO?: boolean
) => {
    if (Iterable.isIterable(data)) {
        return (data as ImmutableIterable).setIn(paths, value);
    }

    return _.set(immutablePOJO ? _.cloneDeep(data) : data, paths, value);
};

// Helper to convert object from immutable to POJO. Return POJO as is if not immutable.
export const amphibiousToJS = (data: object | ImmutableIterable) => {
    if (Iterable.isIterable(data)) {
        return (data as ImmutableIterable).toJS();
    }

    return data;
};

const traverseArrayCaseStyle = (arr: any[], onTransform: (key: string) => string) => {
    arr.forEach((item) => {
        if (item) {
            if (_.isObject(item)) {
                transformCaseStyle(item, onTransform);
            } else if (Array.isArray(item)) {
                traverseArrayCaseStyle(item, onTransform);
            }
        }
    });
};

const traverseCaseStyle = (obj: any, onTransform: (key: string) => string) => {
    Object.keys(obj).forEach((key) => {
        if (obj[key]) {
            if (_.isObject(obj[key])) {
                traverseCaseStyle(obj[key], onTransform);
            } else if (Array.isArray(obj[key])) {
                traverseArrayCaseStyle(obj[key], onTransform);
            }
        }

        const newKey = (onTransform && onTransform(key)) || key;
        if (newKey !== key) {
            obj[newKey] = obj[key];
            delete obj[key];
        }
    });
    return obj;
};

export const transformCaseStyle = (obj: any, onTransform: (key: string) => string) => {
    if (!obj || !_.isObject(obj)) {
        return obj;
    }
    const traverseObj = Object.assign({}, obj);
    return traverseCaseStyle(traverseObj, onTransform);
};

/**
 * To be compatible with React state change detection, and to prevent mutating original objects,
 * we often use object spread to create new objects on the path to a property we want to update.
 *
 * const newItem = {
 *  ...item,
 *  fields: {
 *      ...item.fields,
 *      id: {
 *          string_value: uuid(),
 *      }
 *  }
 * }
 *
 * This utility achieves the same effect using a combination of lodash `setWith` and `clone`.
 *
 * const newItem = setIn(item, 'fields.id.string_value', uuid())
 *
 * @param obj original object that you want to preserve and NOT mutate
 * @param path path to the property you want to update, e.g. 'fields.id.string_value'
 * @param value new value at the path
 * @returns copy of the original object, with all new objects on path
 */
export const setIn = (obj, path, value) => _.setWith(_.clone(obj), path, value, _.clone);

export type MemoFunction<F extends AnyFunction> = F & _.MemoizedFunction;
export type MemoizeMethods<T extends object> = {
    [K in keyof Omit<T, "invalidate">]: T[K] extends AnyFunction ? MemoFunction<T[K]> : T[K];
} & {
    /**
     * Clears all memoized methods
     */
    invalidate: () => void;
};

/**
 * Creates a copy of `target` where all methods are memoised.
 *
 * @param target {Object} The object to memoize methods of.
 */
export const memoizeMethods = <T extends object>(target: T): MemoizeMethods<T> => {
    const result = (Object.keys(target).filter((key) => key !== "invalidate") as [keyof Omit<T, "invalidate">]).reduce(
        (acc, key) => {
            const value: any = target[key];
            acc[key] = _.isFunction(value) ? _.memoize(value) : value;
            return acc;
        },
        {
            invalidate: () => invalidateMemoized(result),
        } as MemoizeMethods<T>
    );
    return result;
};

/**
 * Accepts an object and clears all memoized methods on it, if any.
 *
 * See {@link memoizeMethods}
 */
export const invalidateMemoized = <T extends object>(target: T) =>
    (Object.keys(target) as [keyof T]).forEach((key) => {
        const value: any = target[key];
        if (_.isFunction(value)) {
            (value as MemoFunction<typeof value>).cache?.clear();
        }
    });

export type CompareProp<T extends object> = <K extends keyof T>(value: T[K], prev: T[K], key: K) => boolean;
const defaultCompareProp: CompareProp<any> = (value, prev) => Object.is(value, prev);

/**
 * Creates an object containing props of 'updates', which are not equal to those of `target`.
 * Otherwise, if no updates or all `updates` props equal, returns `undefined`.
 *
 * @param target {Object} An object props, props of which to compare to.
 * @param updates {Object} A subset of `target`'s props.
 * @param compare {<K extends keyof T>(value: T[K], prev: T[K], key: K) => boolean} Optional, prop value comparer. If not provided, identity comparison is used.
 */
export const getDiff = <T extends object>(
    // suppress prettier please
    target: T,
    updates: Partial<T>,
    compare: CompareProp<T> = defaultCompareProp
): Partial<T> | undefined =>
    (Object.entries(updates) as Array<[keyof T, T[keyof T]]>).reduce((acc, [key, value]) => {
        let isPropEqual = compare?.(value, target[key], key);
        if (isPropEqual === undefined) {
            isPropEqual = defaultCompareProp(value, target[key], key);
        }
        if (!isPropEqual) {
            if (!acc) {
                acc = {};
            }
            acc[key] = value;
        }
        return acc;
    }, undefined as Partial<T>);

const omitUndefined = (o: Parameters<typeof _.omitBy>[0]) => _.omitBy(o, _.isUndefined);

const noUndefinedComparer = (a: any, b: any) => {
    if (
        // only non-array objects with any props with values of undefined to be customized
        _.isObject(a) &&
        _.isObject(b) &&
        !_.isArray(a) &&
        !_.isArray(b) &&
        (_.includes(a, undefined) || _.includes(b, undefined))
    ) {
        return _.isEqualWith(omitUndefined(a), omitUndefined(b), noUndefinedComparer);
    }
};

export const isEqualWithDefined = (a: any, b: any) => _.isEqualWith(a, b, noUndefinedComparer);

/**
 * Provided the `F` is a function, unwraps its parameters and, if the parameter type is an array, return the type of the array item;
 * otherwise `any`.
 *
 * @example
 *    type Fun = (values: number[]) => void;
 *    type ParamElemType = ParamsArrayType<Fun>;
 *    // number
 */
type ParamsArrayType<F extends (callArgs: any[]) => any> = F extends (callArgs: (infer T)[]) => any ? T : any;

/**
 * Represents a function type returned by {@link debounceWithCalls}. It is a cancelable function, which accepts a single
 * arguments to dispatch to the `debounceWithCalls`-wrapped function (see an example there).
 */
export type DebounceWithCallsFunction<F extends AnyFunction> = {
    (arg: ParamsArrayType<F>): ReturnType<F> | undefined;
} & Pick<_.DebouncedFunc<F>, "cancel" | "flush">;

/**
 * Almost the same as lodash {@link _.debounce} with the difference that the target wrapped
 * function accepts not only the last call args but the array of all calls
 * including previously debounced ones, if ever was.
 *
 * Returns a cancellable debounced function, which accepts arguments type that
 * represents the target function argument array element.
 *
 * @example
 *
 *    type Event =
 *      | { type: "mouseup" | "mousedown"; x: number; y: number }
 *      | { type: "yell" | "whisper"; text: string };
 *
 *    const processEvents = debounceWithCalls(
 *      // debounced function accepts an array of Event that are dispatched below
 *      (events: Event[]) => {
 *        console.log(events);
 *        // [
 *        //   { type: "mousedown", x: 20, y: 30 },
 *        //   { type: "mouseup", x: 50, y: 50 },
 *        //   { type: "yell", text: "rebel" },
 *        //   { type: "whisper", text: "careless" }
 *        // ]
 *      },
 *      // collect dispatched Events and starts to process 1000ms after the last dispatch.
 *      1000
 *    );
 *
 *    // accepts an Event argument
 *    processEvents({ type: "mousedown", x: 20, y: 30 });
 *    processEvents({ type: "mouseup", x: 50, y: 50 });
 *    processEvents({ type: "yell", text: "rebel" });
 *    processEvents({ type: "whisper", text: "careless" });
 *
 * @param {Function} fn The target function which accepts the array of the
 * @param {number} wait Period to wait before calling the wrapped function, in `ms`.
 * @param {_.DebounceSettings} options
 */
export const debounceWithCalls = <F extends AnyFunction>(
    fn: F,
    wait?: number,
    options?: _.DebounceSettings
): DebounceWithCallsFunction<F> => {
    let callArgs: ParamsArrayType<F>[] = [];

    const debounced = _.debounce(
        function () {
            const result = fn.call(this, callArgs);
            callArgs = [];
            return result;
        },
        wait,
        options
    );

    return Object.assign(
        function (...args) {
            callArgs.push(args as ParamsArrayType<F>);
            return debounced();
        },
        {
            cancel: debounced.cancel,
            flush: debounced.flush,
        }
    ) as DebounceWithCallsFunction<F>;
};

type AnyObject = { [key: string]: any };

export const tryParse = (str: string, defaultObj: AnyObject): AnyObject => {
    let returnedObj;
    try {
        returnedObj = JSON.parse(str);

        if (nullish(returnedObj)) {
            returnedObj = defaultObj;
        }
    } catch (ex) {
        returnedObj = defaultObj;
    }

    return returnedObj;
};
